import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import { qk, useContextSources } from "../../api/queries";
import type { ContextSource, ContextSourceKind } from "../../api/types";
import { useDialogs } from "../../components/Dialogs";
import { JsonField } from "../../components/JsonField";
import { Pill } from "../../components/Pill";
import { ErrorBox, Spinner } from "../../components/Spinner";

const KINDS: { value: ContextSourceKind; label: string; example: Record<string, unknown> }[] = [
  { value: "mcp_http", label: "MCP server (HTTP)", example: { url: "https://example.com/mcp", headers: {} } },
  { value: "mcp_stdio", label: "MCP server (stdio)", example: { command: "npx", args: ["-y", "some-mcp-server"], env: {} } },
  { value: "directory", label: "Mounted directory", example: { path: "/mnt/docs" } },
  { value: "note", label: "Note", example: { text: "" } },
];

export function ContextSourceSettings({ pid }: { pid: string }) {
  const { data, isLoading, error } = useContextSources(pid);
  const [editing, setEditing] = useState<ContextSource | "new" | null>(null);
  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  return (
    <div>
      <p className="muted">
        External systems agents may read for reference. Grant them per environment template via{" "}
        <code>context_source_ids</code>; tasks can narrow the set.
      </p>
      <div className="list">
        {data?.map((s) =>
          editing !== "new" && editing?.id === s.id ? (
            <SourceForm key={s.id} pid={pid} initial={s} onDone={() => setEditing(null)} />
          ) : (
            <div key={s.id} className="list-row">
              <div className="list-main">
                <strong>{s.name}</strong> <Pill color="gray">{s.kind}</Pill>
                {s.description && <div className="muted small">{s.description}</div>}
              </div>
              <button className="btn btn-sm" onClick={() => setEditing(s)}>
                Edit
              </button>
            </div>
          ),
        )}
        {data?.length === 0 && <p className="muted">No context sources yet.</p>}
      </div>
      {editing === "new" ? (
        <SourceForm pid={pid} onDone={() => setEditing(null)} />
      ) : (
        <button className="btn" onClick={() => setEditing("new")}>
          New context source
        </button>
      )}
    </div>
  );
}

function SourceForm({ pid, initial, onDone }: { pid: string; initial?: ContextSource; onDone: () => void }) {
  const qc = useQueryClient();
  const { confirm } = useDialogs();
  const [name, setName] = useState(initial?.name ?? "");
  const [kind, setKind] = useState<ContextSourceKind>(initial?.kind ?? "mcp_http");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [config, setConfig] = useState(JSON.stringify(initial?.config ?? KINDS[0].example, null, 2));
  const [touched, setTouched] = useState(!!initial);
  const [valid, setValid] = useState(true);
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.contextSources(pid) });

  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const body = { name, kind, description, config: JSON.parse(config || "{}") as Record<string, unknown> };
      return initial ? api.patchContextSource(initial.id, body) : api.createContextSource(pid, body);
    },
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });
  const del = useMutation({
    mutationFn: () => api.deleteContextSource(initial!.id),
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });

  return (
    <div className="edit-card form-stack">
      <div className="field-row">
        <label className="field">
          <span className="field-label">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Kind</span>
          <select
            className="input"
            value={kind}
            onChange={(e) => {
              const k = e.target.value as ContextSourceKind;
              setKind(k);
              if (!touched) setConfig(JSON.stringify(KINDS.find((x) => x.value === k)!.example, null, 2));
            }}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span className="field-label">Description (shown to agents)</span>
        <textarea className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <JsonField
        label="Config (JSON)"
        value={config}
        rows={8}
        help="Use ${SECRET_NAME} to reference project secrets."
        onChange={(t, ok) => {
          setTouched(true);
          setConfig(t);
          setValid(ok);
        }}
      />
      {save.error && <div className="error-box">{save.error.message}</div>}
      <div className="row-actions">
        {initial && (
          <button
            className="btn btn-ghost-danger"
            onClick={async () => {
              if (await confirm({ title: `Delete ${initial.name}?`, danger: true, confirmLabel: "Delete" })) del.mutate();
            }}
          >
            Delete
          </button>
        )}
        <span className="spacer" />
        <button className="btn" onClick={onDone}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          {initial ? "Save" : "Create"}
        </button>
      </div>
    </div>
  );
}
